import { createRouter, createWebHistory } from "vue-router";
import { guestRoutes } from "./guestRoutes";
import { adminRoutes } from "./adminRoutes";
import { clientRoutes } from "./clientRoutes";
import { Role } from "@/interfaces/Role";
import { useGlobalStore } from "@/stores/globalStore";
import i18n from "@/i18n";

const routes = [
  ...guestRoutes,
  ...adminRoutes,
  ...clientRoutes,
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: () => import("@/views/HomeView.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

const getHomeRouteForRole = (role: Role) => {
  switch (role) {
    case Role.Admin:
      return "/admin/dashboard/overview";
    case Role.Client:
    case Role.ManagedUser:
    case Role.StandaloneUser:
      return "/dashboard/overview";
    default:
      return "/";
  }
};

const getRoleForRouting = (role: Role): Role => {
  if (role === Role.ManagedUser || role === Role.StandaloneUser) {
    return Role.Client;
  }
  return role;
};

router.beforeEach((to, from, next) => {
  const authStore = useGlobalStore();
  const isAuthenticated = authStore.isAuthenticated;
  const userRole = authStore.user?.role as Role;
  const routingRole = getRoleForRouting(userRole);

  // Provjera da li ruta zahtijeva autentifikaciju
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

  if (requiresAuth && !isAuthenticated) {
    // Ako ruta zahtijeva autentifikaciju, a korisnik nije prijavljen, preusmjeravamo ga na login stranicu
    next("/login");
  } else if (isAuthenticated) {
    // Allow authenticated users to access the Shopify OAuth callback
    if (to.path === "/auth/callback") {
      next();
      return;
    }
    // Ako je korisnik prijavljen, provjeravamo da li ima dozvolu za pristup ruti
    const hasPermission = to.matched.every((record) => {
      // Ako ruta nema definisanu ulogu, dozvoljavamo pristup
      if (!record.meta.role) return true;

      // Provjeravamo da li korisnikova uloga odgovara ulozi definisanoj u ruti
      return record.meta.role === routingRole;
    });

    if (hasPermission) {
      // Ako je ruta '/' i korisnik je autentifikovan, preusmjeravamo ga na odgovarajuću početnu stranicu
      if (to.path === "/") {
        next(getHomeRouteForRole(userRole));
      } else if (
        (routingRole === Role.Admin &&
          to.path.startsWith("/admin/dashboard")) ||
        to.path.startsWith("/admin/tools")
      ) {
        // Provjeravamo da li je admin ruta i da li zahtijeva `clientId`
        if (authStore.selectedClientId) {
          if (to.params.clientId !== authStore.selectedClientId) {
            // Koristimo replace sa pravilno specificiranim parametrima
            router.replace({
              name: to.name as string, // Ovdje koristimo `name` ili `path`
              params: { ...to.params, clientId: authStore.selectedClientId },
              query: to.query,
              hash: to.hash,
            });
          } else {
            next();
          }
        } else {
          // Ako nema `selectedClientId`, uklanjamo clientId iz URL-a
          const { clientId, ...remainingParams } = to.params;
          if (clientId) {
            // Koristimo replace sa pravilno specificiranim parametrima
            router.replace({
              name: to.name as string, // Ovdje koristimo `name` ili `path`
              params: remainingParams,
              query: to.query,
              hash: to.hash,
            });
          } else {
            next();
          }
        }
      } else {
        next(); // Korisnik ima dozvolu, nastavljamo na željenu rutu
      }
    } else {
      // Korisnik nema dozvolu, preusmjeravamo ga na odgovarajuću početnu stranicu
      next(getHomeRouteForRole(userRole));
    }
  } else {
    // Ako ruta ne zahtijeva autentifikaciju, dozvoljavamo pristup
    next();
  }
});

router.afterEach((to) => {
  const defaultTitle = "Shopify Analytics";
  const title = (to.meta?.title as string)
    ? i18n.global.t(to.meta.title as string)
    : defaultTitle;
  document.title = title;
});

export default router;
